/**
 * @brisa/ui · admin/dashboard-shell/DashboardShell
 *
 * Feature: admin-basico-login (W3.2 v1.2) · UI_SPEC §10 Tema Neutro Admin
 *
 * Casca completa do dashboard admin — substitui o `AdminHeader` facelift
 * descartado em v1.2. Composição shadcn padrão:
 *
 *   <SidebarProvider>
 *     <Sidebar>                ← navegação esquerda, collapsible
 *       <SidebarHeader>"BRISA ADMIN" + env badge
 *       <SidebarContent>menu items (W3.2: só "Dashboard"; W3.3+ adiciona Clubs)
 *       <SidebarFooter>UserMenu dropdown
 *     <SidebarInset>           ← main area com topbar + children
 *       <header>SidebarTrigger + Breadcrumb + page title
 *       <main>{children}
 *
 * Apresentacional puro — sem hooks de servidor. O wrapper Server Component
 * que resolve `userName` (Supabase), `env` (BRISA_ENV) e injeta o
 * `<SignOutButton/>` client vive em `apps/web/app/admin/_components/` na
 * Fase 2 desta feature (Bloco 7 do plano).
 *
 * Neutro em gênero (regra durável do PL): copy não usa "Bem-vinda/o" —
 * apenas "Olá, {primeiro nome}." em PT e "Hola, {primeiro nome}." em ES.
 *
 * Sem 'use client' — SidebarProvider já tem 'use client' internamente.
 */
import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@brisa/ui/lib/utils';
import { Badge } from '@brisa/ui/components/ui/badge';
import { Separator } from '@brisa/ui/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@brisa/ui/components/ui/sidebar';

export type DashboardShellEnv = 'DEV' | 'PRD';
export type DashboardShellLocale = 'pt' | 'es';

export interface DashboardShellNavItem {
  label: string;
  icon: LucideIcon;
  href: string;
  isActive?: boolean;
}

export interface DashboardShellProps {
  /** Primeiro nome do operador (já derivado pelo Server wrapper). Ex: "Bruno". */
  userName: string;
  env: DashboardShellEnv;
  locale?: DashboardShellLocale;
  /** Itens da nav lateral. W3.2 entrega só "Dashboard"; W3.3+ adiciona Clubs etc. */
  nav: DashboardShellNavItem[];
  /**
   * Slot para o botão Client que dispara `signOutAdmin` Server Action.
   * Tipicamente `<SignOutAdminButton/>` envolvendo um `<Button variant="ghost">`.
   * Renderizado no footer da Sidebar (sem o slot, footer apenas mostra userMenu).
   */
  signOutSlot?: React.ReactNode;
  /**
   * Conteúdo da breadcrumb no topbar. Ex: `<Breadcrumb>...</Breadcrumb>` shadcn.
   * Quando omitido, o topbar mostra só o page title derivado do nav ativo.
   */
  breadcrumb?: React.ReactNode;
  /** Título da página renderizado no topbar quando `breadcrumb` está ausente. */
  pageTitle?: string;
  /** Wordmark da sidebar header. Default "BRISA ADMIN". */
  wordmark?: string;
  /** Conteúdo principal (renderizado dentro de SidebarInset). */
  children: React.ReactNode;
}

const COPY: Record<DashboardShellLocale, { greeting: (name: string) => string; navGroupOps: string; envDev: string; envPrd: string }> = {
  pt: {
    greeting: (name) => `Olá, ${name}.`,
    navGroupOps: 'Operações',
    envDev: 'Ambiente de desenvolvimento',
    envPrd: 'Ambiente de produção',
  },
  es: {
    greeting: (name) => `Hola, ${name}.`,
    navGroupOps: 'Operaciones',
    envDev: 'Entorno de desarrollo',
    envPrd: 'Entorno de producción',
  },
};

export function DashboardShell({
  userName,
  env,
  locale = 'pt',
  nav,
  signOutSlot,
  breadcrumb,
  pageTitle,
  wordmark = 'BRISA ADMIN',
  children,
}: DashboardShellProps) {
  const copy = COPY[locale];
  const isDev = env === 'DEV';

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <div className="flex items-center gap-2 px-2 py-2">
            <span
              data-slot="dashboard-shell-wordmark"
              className="text-sm font-semibold tracking-[0.04em] text-sidebar-foreground group-data-[collapsible=icon]:hidden"
            >
              {wordmark}
            </span>
            <Badge
              variant="outline"
              data-slot="dashboard-shell-env-badge"
              aria-label={isDev ? copy.envDev : copy.envPrd}
              className={cn(
                'ml-auto text-[10px] font-semibold uppercase tracking-[0.06em] group-data-[collapsible=icon]:hidden',
                isDev
                  ? 'border-amber-300 bg-amber-100 text-amber-900'
                  : 'border-sidebar-border bg-sidebar-accent text-sidebar-foreground',
              )}
            >
              {env}
            </Badge>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>{copy.navGroupOps}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {nav.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.label}
                      isActive={item.isActive}
                    >
                      <a href={item.href}>
                        <item.icon />
                        <span>{item.label}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <div
            data-slot="dashboard-shell-user-block"
            className="flex flex-col gap-1 px-2 py-2 text-xs text-sidebar-foreground/80 group-data-[collapsible=icon]:hidden"
          >
            <span data-slot="dashboard-shell-greeting" className="font-medium text-sidebar-foreground">
              {copy.greeting(userName)}
            </span>
          </div>
          {signOutSlot ? (
            <div data-slot="dashboard-shell-sign-out-slot" className="px-2 pb-2">
              {signOutSlot}
            </div>
          ) : null}
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 items-center gap-2 border-b bg-background px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mx-1 h-4" />
          {breadcrumb ?? (
            <span data-slot="dashboard-shell-page-title" className="text-sm font-medium">
              {pageTitle}
            </span>
          )}
        </header>
        <main data-slot="dashboard-shell-main" className="flex-1 p-6">
          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
